import { useContext, useEffect, useMemo, useState } from "react";

import { Paper, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { AppContext } from "App";
import EmptyCard from "components/Cards/EmptyCard";
import SkeletonTable from "components/shared/table-skeleton/table-skeleton";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import { useSnackbar } from "notistack";
import { formatPrice } from "utility/formatters";

import useFinanceReport from "../../Financial/useFinanceReport";
import { ReportsIcon } from "../../Icons/Icons";
import Letterhead from "../../Letterhead";
import { formatText } from "./helpers";

export default function IncomeStatement() {
  const { enqueueSnackbar } = useSnackbar();
  const { globalState, globalDispatch } = useContext(AppContext);
  const [currentYear, setCurrentYear] = useState(globalState.crop_year);

  // Sync global crop_year with local state
  useEffect(() => {
    if (globalState.crop_year !== currentYear) {
      globalDispatch({ type: "update", payload: { key: "crop_year", value: currentYear } });
    }
  }, [currentYear, globalState.crop_year, globalDispatch]);

  const { isLoadingIncomeStatement, incomeStatement, isLoadingBudget, budget, ratios } = useFinanceReport({
    onError: (err) => enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { variant: "error" }),
    currentYear,
  });

  // Memoize derived data to prevent unnecessary recalculations
  const { revenue_containers = [], expenses_containers = [] } = incomeStatement || {};
  const { budgetCommodities = [] } = budget || {};
  const totals = useMemo(
    () => [
      {
        labelLeft: "Total Revenue from Operations",
        valueLeft: ratios?.revenue_operations,
        labelRight: "Total Costs and Expenses",
        valueRight: budget?.expenses?.total + incomeStatement?.expenses?.total,
      },
      {
        labelLeft: "Farm Revenue",
        valueLeft: budget?.revenue,
        labelRight: "Production Cost",
        valueRight: budget?.production_cost,
      },
      {
        labelLeft: "Other Revenue",
        valueLeft: incomeStatement?.revenue?.total,
        labelRight: "Other Expenses",
        valueRight: incomeStatement?.expenses?.total,
      },
      {
        labelLeft: "",
        valueLeft: null,
        labelRight: "Depreciation Expense",
        valueRight: incomeStatement?.expenses_depreciation?.total,
      },
      {
        labelLeft: "",
        valueLeft: null,
        labelRight: "Total Interest Expense",
        valueRight: incomeStatement?.expenses_interest?.total,
      },
      {
        labelLeft: "",
        valueLeft: null,
        labelRight: "Net Income",
        valueRight: ratios?.net_income,
      },
    ],
    [ratios, budget, incomeStatement]
  );

  const tableCells = useMemo(() => {
    const maxLength = Math.max(revenue_containers.length, expenses_containers.length);
    return Array.from({ length: maxLength }, (_, idx) => [
      revenue_containers[idx] ? formatText(revenue_containers[idx]) : "",
      revenue_containers[idx] ? incomeStatement[revenue_containers[idx]] || {} : {},
      expenses_containers[idx] ? formatText(expenses_containers[idx]) : "",
      expenses_containers[idx] ? incomeStatement[expenses_containers[idx]] || {} : {},
    ]);
  }, [revenue_containers, expenses_containers, incomeStatement]);

  // Memoized row rendering functions
  const renderRows = useMemo(
    () => (revenueData, expenseData) => {
      const revenueValues = revenueData?.values || [];
      const expenseValues = expenseData?.values || [];
      return Array.from({ length: Math.max(revenueValues.length, expenseValues.length) }, (_, index) => (
        <TableRow key={index}>
          <TableCell sx={{ pl: 4 }}>{formatText(revenueValues[index]?.item)}</TableCell>
          <TableCell align="right">{revenueValues[index] ? formatPrice(revenueValues[index].value, 0) : ""}</TableCell>
          <TableCell sx={{ pl: 4 }}>{formatText(expenseValues[index]?.item)}</TableCell>
          <TableCell align="right">{expenseValues[index] ? formatPrice(expenseValues[index].value, 0) : ""}</TableCell>
        </TableRow>
      ));
    },
    []
  );

  const renderBudgetReportRows = useMemo(
    () => (commodities) => {
      if (!Array.isArray(commodities) || !commodities.length) return null;
      return commodities.map((commodity, index) => (
        <TableRow key={commodity.commodity_name + index}>
          <TableCell>
            <Typography sx={{ ml: 2 }}>{commodity.commodity_name}</Typography>
          </TableCell>
          <TableCell align="right">{formatPrice(commodity.revenue, 0)}</TableCell>
          <TableCell>
            <Typography sx={{ ml: 2 }}>{commodity.commodity_name}</Typography>
          </TableCell>
          <TableCell align="right">{formatPrice(commodity.production_cost, 0)}</TableCell>
        </TableRow>
      ));
    },
    []
  );

  // Early return for empty state
  if (!isLoadingIncomeStatement && (!incomeStatement || !Object.keys(incomeStatement).length)) {
    return (
      <>
        <WorkspaceHeader
          title="Income Statement Report"
          category={{
            title: "Reports",
            icon: <ReportsIcon />,
          }}
        />
        <EmptyCard text="No search results" secondaryText="Remove some filters or add a new balance sheet" />
      </>
    );
  }

  return (
    <>
      <div className="no-print">
        <WorkspaceHeader
          title="Income Statement Report"
          category={{
            title: "Reports",
            icon: <ReportsIcon />,
          }}
        />

        <WorkspacePageHeader
          title="Year"
          currentYear={currentYear}
          onYearChange={setCurrentYear}
          isLoading={isLoadingIncomeStatement || isLoadingBudget}
        />
      </div>

      <Paper
        elevation={3}
        className="reportPaper"
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: "background.paper",
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          maxWidth: "1200px",
          width: "fit-content",
          minWidth: "800px",
          "@media print": {
            "&.reportPaper": {
              boxShadow: "none !important",
              border: "none !important",
              borderRadius: "0 !important",
              p: 0,
              backgroundColor: "transparent !important",
            },
          },
        }}
      >
        <div className="show-in-print report-header-with-letterhead">
          <Typography variant="h1" sx={{ mb: 0 }}>
            Income Statement Report - Year: {currentYear}
          </Typography>
          <Letterhead />
        </div>

        <Table size="small" className="financial-report-table reportTable">
          <TableBody>
            <TableRow>
              <TableCell colSpan={2} width={"50%"}>
                <Typography fontWeight={"bold"} fontSize={"1.2em"}>
                  Revenue from Operations
                </Typography>
              </TableCell>
              <TableCell colSpan={2} width={"50%"}>
                <Typography fontWeight={"bold"} fontSize={"1.2em"}>
                  Operating Costs & Expenses
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>

          {isLoadingBudget ? (
            <TableBody>
              <TableRow>
                <TableCell colSpan={4}>
                  <SkeletonTable skeletonTableRows={5} skeletonTableColumn={4} />
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <TableBody className="financial-report-group">
              <TableRow className="highlight-print" sx={{ backgroundColor: "grey.200" }}>
                <TableCell colSpan={2} className="financial-report-table-cell">
                  <Typography>Farm Revenue</Typography>
                </TableCell>
                <TableCell colSpan={2} className="financial-report-table-cell">
                  <Typography>Production Costs</Typography>
                </TableCell>
              </TableRow>
              {renderBudgetReportRows(budgetCommodities)}
            </TableBody>
          )}

          {isLoadingIncomeStatement ? (
            <TableBody>
              <TableRow>
                <TableCell colSpan={4}>
                  <SkeletonTable skeletonTableRows={10} skeletonTableColumn={4} />
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <>
              {tableCells
                .filter(([, leftData, , rightData]) => leftData?.values?.length > 0 || rightData?.values?.length > 0)
                .map(([leftLabel, leftData, rightLabel, rightData], idx) => (
                  <TableBody key={idx} className="financial-report-group">
                    <TableRow className="highlight-print" sx={{ backgroundColor: "grey.200" }}>
                      <TableCell colSpan={2} className="financial-report-table-cell">
                        <Typography>{leftLabel}</Typography>
                      </TableCell>
                      <TableCell colSpan={2} className="financial-report-table-cell">
                        <Typography>{rightLabel}</Typography>
                      </TableCell>
                    </TableRow>
                    {renderRows(leftData, rightData)}
                  </TableBody>
                ))}

              <TableBody>
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography fontWeight={"bold"}>Total</Typography>
                  </TableCell>
                </TableRow>
                {totals.map(({ labelLeft, valueLeft, labelRight, valueRight }, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ pl: 2, fontWeight: "bold" }}>{labelLeft}</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }} align="right">
                      {valueLeft !== null && valueLeft !== undefined ? formatPrice(valueLeft, 0) : ""}
                    </TableCell>
                    <TableCell sx={{ pl: 2, fontWeight: "bold" }}>{labelRight}</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }} align="right">
                      {formatPrice(valueRight, 0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </>
          )}
        </Table>
      </Paper>
    </>
  );
}
